import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import Navbar from '@/components/Navbar';
import ProfileCard from '@/components/ProfileCard';
import TorsArea from '@/components/TorsArea';
import LoansArea from '@/components/LoansArea';
import NotificationPanel from '@/components/NotificationPanel';
import CycleCalculator from '@/components/CycleCalculator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ShieldWarning } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [tors, setTors] = useState([]);
  const [loans, setLoans] = useState([]);
  const [allowLoans, setAllowLoans] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unitsBalance, setUnitsBalance] = useState(0);
  const [torsVersion, setTorsVersion] = useState(0);
  const [kycNudge, setKycNudge] = useState(false);

  const fetchTors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tors`, { withCredentials: true });
      setTors(res.data);
      setTorsVersion(v => v + 1);
    } catch (err) { console.error('Failed to fetch tors:', err); }
  }, []);

  const fetchLoans = useCallback(async () => {
    try {
      const [lRes, cRes] = await Promise.all([
        axios.get(`${API}/loans`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/loans/config`, { withCredentials: true }).catch(() => ({ data: { allow_loans: true } })),
      ]);
      setLoans(lRes.data || []);
      setAllowLoans(!!cRes.data.allow_loans);
    } catch (err) { console.error('Failed to fetch loans:', err); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (err) { console.error('Failed to fetch notifications:', err); }
  }, []);

  const checkKycNudge = useCallback(async () => {
    if (sessionStorage.getItem('kyc_nudge_dismissed')) return;
    try {
      const [nodeRes, kycRes] = await Promise.all([
        axios.get(`${API}/node`, { withCredentials: true }).catch(() => ({ data: { node: null } })),
        axios.get(`${API}/kyc/status`, { withCredentials: true }).catch(() => ({ data: { bank_verified: false } })),
      ]);
      const node = nodeRes.data?.node;
      const active = node && ['paid', 'mandate_active', 'charged'].includes(node.fee_status);
      if (active && !kycRes.data.bank_verified) setKycNudge(true);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchTors(), fetchLoans(), fetchNotifications()]);
      setLoading(false);
      checkKycNudge();
    };
    loadData();
  }, [fetchTors, fetchLoans, fetchNotifications, checkKycNudge]);

  const dismissNudge = () => { sessionStorage.setItem('kyc_nudge_dismissed', '1'); setKycNudge(false); };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`}>
        <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="home-page">
      <Navbar
        unreadCount={unreadCount}
        onNotificationClick={() => setShowNotifications(!showNotifications)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card - Left Column */}
          <div className="lg:col-span-1">
            <ProfileCard user={user} onUpdate={refreshUser} unitsBalance={unitsBalance} />
          </div>

          {/* Tors Area - Right Column */}
          <div className="lg:col-span-2">
            <TorsArea
              tors={tors}
              currency={user?.currency || 'INR'}
              onRefresh={() => { fetchTors(); fetchNotifications(); }}
            />

            {/* Loans (Banks or NBFCs) — only if admin allows loans */}
            {allowLoans && (
              <LoansArea
                loans={loans}
                currency={user?.currency || 'INR'}
                onRefresh={() => { fetchLoans(); fetchNotifications(); setTorsVersion(v => v + 1); }}
              />
            )}

            {/* Cycle Calculator */}
            <CycleCalculator currency={user?.currency || 'INR'} onUnitsChange={setUnitsBalance} refreshKey={torsVersion} />
          </div>
        </div>
      </main>

      {/* Notification Panel */}
      <NotificationPanel
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onRefresh={fetchNotifications}
        onTorRefresh={fetchTors}
      />

      {/* KYC reminder while a clearance cycle is active */}
      <Dialog open={kycNudge} onOpenChange={(v) => { if (!v) dismissNudge(); }}>
        <DialogContent className={`rounded-md max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`} data-testid="kyc-nudge-dialog">
          <DialogHeader>
            <DialogTitle className={`font-outfit text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
              <ShieldWarning size={20} weight="bold" className="text-yellow-400" /> Complete KYC to receive payouts
            </DialogTitle>
            <DialogDescription className={`font-ibm text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
              Your clearance cycle is active. KYC isn't needed to be netted, but you'll need verified bank details before any payout can be released to you. Completing it now makes withdrawals instant.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <button onClick={dismissNudge} className={`flex-1 h-10 font-ibm text-sm font-medium rounded-sm border ${isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`} data-testid="kyc-nudge-later">Later</button>
            <button onClick={() => { dismissNudge(); navigate('/kyc'); }} className="flex-1 h-10 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500" data-testid="kyc-nudge-verify">Complete KYC</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
